/**
 * MESH KIT (2026-10-07 — LIGHT STYLES, shared with PLUMBING STYLES): the small toolkit the
 * detailed fixture bodies are built from, in INCHES under a rotate / translate frame stack —
 * lathe (surfaces of revolution; 4 / 8 segments = pyramids / octagons), tube (round or
 * square sections swept along a path), bar, box, pane (glass, both faces), chain, plus
 * per-ROLE solid groups so one proto carries several materials (OpeningProto.solidGroups).
 */
import { MeshBuilder } from './extrude'
import type { SolidGroup } from './categories/opening/protos'

export type V3 = [number, number, number]
export type M3 = [number, number, number, number, number, number, number, number, number]

export const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
export const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
export const mul = (a: V3, k: number): V3 => [a[0] * k, a[1] * k, a[2] * k]
export const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
export const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
export const norm = (a: V3): V3 => { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l] }

export const ID: M3 = [1, 0, 0, 0, 1, 0, 0, 0, 1]
export const mm = (A: M3, B: M3): M3 => {
  const o = new Array(9).fill(0) as M3
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) for (let k = 0; k < 3; k++) o[r * 3 + c] += A[r * 3 + k] * B[k * 3 + c]
  return o
}
export const mv = (A: M3, v: V3): V3 => [A[0] * v[0] + A[1] * v[1] + A[2] * v[2], A[3] * v[0] + A[4] * v[1] + A[5] * v[2], A[6] * v[0] + A[7] * v[1] + A[8] * v[2]]
export const rotZ = (a: number): M3 => { const c = Math.cos(a), s = Math.sin(a); return [c, -s, 0, s, c, 0, 0, 0, 1] }
export const rotX = (a: number): M3 => { const c = Math.cos(a), s = Math.sin(a); return [1, 0, 0, 0, c, -s, 0, s, c] }

/** a material role: 'glass' routes to the glass buffer, anything else is a solid group */
export type Role = string

/** the fixture mesh kit — inches, under a rotate / translate frame stack (light styles,
 *  plumbing styles) */
export class LB {
  private by = new Map<string, MeshBuilder>()
  glass = new MeshBuilder()
  private cur!: MeshBuilder
  private toGlass = false
  private R: M3 = ID
  private T: V3 = [0, 0, 0]
  private stack: { R: M3; T: V3 }[] = []
  constructor() { this.role('metal') }

  role(r: Role): this {
    if (r === 'glass') { this.toGlass = true; return this }
    this.toGlass = false
    let m = this.by.get(r)
    if (!m) { m = new MeshBuilder(); this.by.set(r, m) }
    this.cur = m
    return this
  }
  /** run `fn` in a sub-frame: rotate by `R`, then move to `t` (both in the current frame) */
  frame(t: V3, R: M3, fn: () => void) {
    this.stack.push({ R: this.R, T: this.T })
    this.T = add(this.T, mv(this.R, t))
    this.R = mm(this.R, R)
    fn()
    const s = this.stack.pop()!
    this.R = s.R; this.T = s.T
  }
  private P(p: V3): number[] { const q = add(this.T, mv(this.R, p)); return [q[0] / 12, q[1] / 12, q[2] / 12] }
  private N(n: V3): number[] { return mv(this.R, n) }
  triN(a: V3, b: V3, c: V3, na: V3, nb: V3, nc: V3) {
    ;(this.toGlass ? this.glass : this.cur).addTriN(this.P(a), this.P(b), this.P(c), this.N(na), this.N(nb), this.N(nc))
  }
  tri(a: V3, b: V3, c: V3) {
    const n = norm(cross(sub(b, a), sub(c, a)))
    this.triN(a, b, c, n, n, n)
  }
  quad(a: V3, b: V3, c: V3, d: V3) { this.tri(a, b, c); this.tri(a, c, d) }

  /**
   * A surface of revolution about the local z axis. `prof` = (r, z) points, traversed TOP →
   * OUT → BOTTOM → IN (clockwise in the r–z half plane) so the faces face out; a thin shell
   * runs down its outside and back up its inside. segs ≤ 8 = flat faceted (a pyramid at 4 with
   * `phase` π/4, an octagon at 8); more = smooth, with a crease wherever the profile turns
   * more than ~35°.
   */
  lathe(prof: [number, number][], segs = 24, phase = 0, a0 = 0, a1 = Math.PI * 2) {
    const flat = segs <= 8
    const full = Math.abs(a1 - a0 - Math.PI * 2) < 1e-6
    const nA = full ? segs : segs + 1
    const ang = (j: number) => phase + a0 + ((a1 - a0) * j) / segs
    const pt = (i: number, j: number): V3 => {
      const a = ang(j), r = prof[i][0]
      return [r * Math.cos(a), r * Math.sin(a), prof[i][1]]
    }
    // per-segment normals in the r–z plane, and the per-end normals (creases kept)
    const segN: [number, number][] = []
    for (let i = 0; i + 1 < prof.length; i++) {
      const dr = prof[i + 1][0] - prof[i][0], dz = prof[i + 1][1] - prof[i][1]
      const l = Math.hypot(dr, dz) || 1
      segN.push([-dz / l, dr / l])
    }
    const endN = (i: number, k: number): [number, number] => {
      const n = segN[k]
      const o = i === k ? segN[k - 1] : segN[k + 1]
      if (!o) return n
      if (n[0] * o[0] + n[1] * o[1] < 0.82) return n
      const s: [number, number] = [n[0] + o[0], n[1] + o[1]]
      const l = Math.hypot(s[0], s[1]) || 1
      return [s[0] / l, s[1] / l]
    }
    const nrm = (rz: [number, number], j: number): V3 => { const a = ang(j); return [rz[0] * Math.cos(a), rz[0] * Math.sin(a), rz[1]] }
    for (let k = 0; k + 1 < prof.length; k++) {
      if (prof[k][0] < 1e-6 && prof[k + 1][0] < 1e-6) continue
      if (Math.abs(prof[k][0] - prof[k + 1][0]) < 1e-9 && Math.abs(prof[k][1] - prof[k + 1][1]) < 1e-9) continue
      const n0 = endN(k, k), n1 = endN(k + 1, k)
      for (let j = 0; j < (full ? segs : segs); j++) {
        const j1 = full ? (j + 1) % nA : j + 1
        const A = pt(k, j), B = pt(k + 1, j), C = pt(k + 1, j1), D = pt(k, j1)
        if (flat) {
          if (prof[k][0] < 1e-6) this.tri(A, B, C)
          else if (prof[k + 1][0] < 1e-6) this.tri(A, B, D)
          else this.quad(A, B, C, D)
        } else {
          const nA0 = nrm(n0, j), nB0 = nrm(n1, j), nB1 = nrm(n1, j1), nA1 = nrm(n0, j1)
          if (prof[k][0] < 1e-6) this.triN(A, B, C, nA0, nB0, nB1)
          else if (prof[k + 1][0] < 1e-6) this.triN(A, B, D, nA0, nB0, nA1)
          else { this.triN(A, B, C, nA0, nB0, nB1); this.triN(A, C, D, nA0, nB1, nA1) }
        }
      }
    }
  }
  /** a lathe placed at a point (and optionally tipped by R) */
  latheAt(c: V3, prof: [number, number][], segs = 24, phase = 0, R: M3 = ID) {
    this.frame(c, R, () => this.lathe(prof, segs, phase))
  }
  /** a closed solid disc / drum: r, z0..z1, capped */
  drum(c: V3, r: number, z0: number, z1: number, segs = 24) {
    this.latheAt(c, [[0, z1], [r, z1], [r, z0], [0, z0]], segs)
  }
  sphere(c: V3, r: number, segs = 16) {
    const prof: [number, number][] = []
    const n = Math.max(6, Math.round(segs / 2))
    for (let i = 0; i <= n; i++) { const a = Math.PI / 2 - (Math.PI * i) / n; prof.push([Math.cos(a) * r, Math.sin(a) * r]) }
    this.latheAt(c, prof, segs)
  }

  /** a round (segs > 4) or square (segs = 4) section swept along `path`, capped */
  tube(path: V3[], r: number, segs = 10, caps = true) {
    if (path.length < 2) return
    const tans: V3[] = path.map((p, i) => norm(sub(path[Math.min(i + 1, path.length - 1)], path[Math.max(i - 1, 0)])))
    const t0 = tans[0]
    let n = norm(Math.abs(t0[2]) < 0.9 ? cross(t0, [0, 0, 1]) : cross(t0, [1, 0, 0]))
    const rings: { p: V3[]; d: V3[] }[] = []
    const flat = segs <= 4
    const ph = flat ? Math.PI / 4 : 0
    for (let i = 0; i < path.length; i++) {
      const t = tans[i]
      n = norm(sub(n, mul(t, dot(n, t))))
      const b = cross(t, n)
      const p: V3[] = [], d: V3[] = []
      for (let k = 0; k < segs; k++) {
        const a = ph + (Math.PI * 2 * k) / segs
        const dir = add(mul(n, Math.cos(a)), mul(b, Math.sin(a)))
        d.push(dir); p.push(add(path[i], mul(dir, flat ? r * Math.SQRT2 : r)))
      }
      rings.push({ p, d })
    }
    for (let i = 0; i + 1 < rings.length; i++) {
      const A = rings[i], B = rings[i + 1]
      for (let k = 0; k < segs; k++) {
        const k1 = (k + 1) % segs
        if (flat) this.quad(A.p[k], A.p[k1], B.p[k1], B.p[k])
        else {
          this.triN(A.p[k], A.p[k1], B.p[k1], A.d[k], A.d[k1], B.d[k1])
          this.triN(A.p[k], B.p[k1], B.p[k], A.d[k], B.d[k1], B.d[k])
        }
      }
    }
    if (!caps) return
    const cap = (i: number, s: number) => {
      const R = rings[i], c = path[i], nn = mul(tans[i], s)
      for (let k = 0; k < segs; k++) {
        const k1 = (k + 1) % segs
        if (s < 0) this.triN(c, R.p[k1], R.p[k], nn, nn, nn)
        else this.triN(c, R.p[k], R.p[k1], nn, nn, nn)
      }
    }
    cap(0, -1); cap(path.length - 1, 1)
  }
  cyl(a: V3, b: V3, r: number, segs = 12) { this.tube([a, b], r, segs) }
  /** a square bar of half-width h from a to b */
  bar(a: V3, b: V3, h: number) { this.tube([a, b], h, 4) }
  /** a closed ring (torus-like) of section r in the plane ⊥ `axis` at c, radius R */
  ring(c: V3, R: number, r: number, axis: 'z' | 'x' | 'y' = 'z', segs = 28, sec = 8) {
    const pts: V3[] = []
    for (let i = 0; i <= segs; i++) {
      const a = (Math.PI * 2 * i) / segs
      const u = Math.cos(a) * R, v = Math.sin(a) * R
      pts.push(axis === 'z' ? [c[0] + u, c[1] + v, c[2]] : axis === 'x' ? [c[0], c[1] + u, c[2] + v] : [c[0] + u, c[1], c[2] + v])
    }
    this.tube(pts, r, sec, false)
  }
  box(x0: number, x1: number, y0: number, y1: number, z0: number, z1: number) {
    const p: V3[] = [[x0, y0, z0], [x1, y0, z0], [x1, y1, z0], [x0, y1, z0], [x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]]
    this.quad(p[4], p[5], p[6], p[7]); this.quad(p[0], p[3], p[2], p[1])
    this.quad(p[0], p[1], p[5], p[4]); this.quad(p[1], p[2], p[6], p[5])
    this.quad(p[2], p[3], p[7], p[6]); this.quad(p[3], p[0], p[4], p[7])
  }
  /** a flat plate on the wall: a CONVEX outline in x–z (CCW seen from the room), y0..y1 */
  plateY(poly: [number, number][], y0: number, y1: number) {
    const n = poly.length
    const F = poly.map(([x, z]) => [x, y1, z] as V3), B = poly.map(([x, z]) => [x, y0, z] as V3)
    for (let i = 2; i < n; i++) { this.tri(F[0], F[i], F[i - 1]); this.tri(B[0], B[i - 1], B[i]) }
    for (let i = 0; i < n; i++) { const j = (i + 1) % n; this.quad(B[i], F[i], F[j], B[j]) }
  }
  /** a glass pane through 4 corners — both faces */
  pane(a: V3, b: V3, c: V3, d: V3) {
    const was = this.toGlass
    this.toGlass = true
    this.quad(a, b, c, d); this.quad(a, d, c, b)
    this.toGlass = was
  }
  /** oval chain links from a (top) down to b */
  chain(a: V3, b: V3, linkL = 1.3, linkW = 0.75, wire = 0.13) {
    const axis = sub(b, a), L = Math.hypot(axis[0], axis[1], axis[2])
    if (L < 0.5) return
    const u = norm(axis)
    const pitch = linkL - wire * 2.2
    const n = Math.max(1, Math.round(L / pitch))
    const side0 = norm(Math.abs(u[2]) < 0.9 ? cross(u, [0, 0, 1]) : cross(u, [1, 0, 0]))
    for (let i = 0; i < n; i++) {
      const c = add(a, mul(u, pitch * (i + 0.5) * (L / (pitch * n))))
      const side = i % 2 ? cross(u, side0) : side0
      const pts: V3[] = []
      for (let k = 0; k <= 16; k++) {
        const t = (Math.PI * 2 * k) / 16
        const along = (Math.sin(t) * (linkL / 2 - wire)), across = Math.cos(t) * (linkW / 2 - wire)
        pts.push(add(c, add(mul(u, along), mul(side, across))))
      }
      this.tube(pts, wire, 5, false)
    }
  }
  /**
   * PLUMBING STYLES: a smooth skin through a stack of RINGS (each the same point count, CCW seen
   * from +z). Rings going UP give an outward-facing skin; going DOWN, an inward one (a bowl's
   * inside). Vertex normals average the faces round each ring point; a ring index listed in
   * `sharp` keeps a crease there.
   */
  loft(rings: V3[][], sharp: number[] = []) {
    if (rings.length < 2) return
    const n = rings[0].length
    const faceN = (i: number, j: number): V3 => {
      const j1 = (j + 1) % n
      const a = rings[i][j], b = rings[i][j1], d = rings[i + 1][j]
      return cross(sub(b, a), sub(d, a))
    }
    const acc: V3[][] = rings.map((r) => r.map(() => [0, 0, 0] as V3))
    for (let i = 0; i + 1 < rings.length; i++) for (let j = 0; j < n; j++) {
      const f = faceN(i, j), j1 = (j + 1) % n
      for (const [ii, jj] of [[i, j], [i, j1], [i + 1, j], [i + 1, j1]] as [number, number][]) acc[ii][jj] = add(acc[ii][jj], f)
    }
    // at a sharp ring each side takes only its own band's faces
    const nrm = (ring: number, j: number, band: number): V3 => {
      if (!sharp.includes(ring)) return norm(acc[ring][j])
      const jp = (j - 1 + n) % n
      return norm(add(faceN(band, j), faceN(band, jp)))
    }
    for (let i = 0; i + 1 < rings.length; i++) for (let j = 0; j < n; j++) {
      const j1 = (j + 1) % n
      const a = rings[i][j], b = rings[i][j1], c = rings[i + 1][j1], d = rings[i + 1][j]
      this.triN(a, b, c, nrm(i, j, i), nrm(i, j1, i), nrm(i + 1, j1, i))
      this.triN(a, c, d, nrm(i, j, i), nrm(i + 1, j1, i), nrm(i + 1, j, i))
    }
  }
  /** a flat-ish band between two loops of equal count (outer and inner, both CCW) — faces UP;
   *  `down` flips it */
  bridge(outer: V3[], inner: V3[], down = false) {
    const n = outer.length
    for (let j = 0; j < n; j++) {
      const j1 = (j + 1) % n
      if (down) this.quad(outer[j], inner[j], inner[j1], outer[j1])
      else this.quad(outer[j], outer[j1], inner[j1], inner[j])
    }
  }
  /** a fan cap over a CCW loop (up = facing +z) */
  capLoop(loop: V3[], up = true) {
    const n = loop.length
    const c: V3 = [0, 0, 0]
    for (const p of loop) { c[0] += p[0] / n; c[1] += p[1] / n; c[2] += p[2] / n }
    for (let j = 0; j < n; j++) {
      const j1 = (j + 1) % n
      if (up) this.tri(c, loop[j], loop[j1])
      else this.tri(c, loop[j1], loop[j])
    }
  }
  /** a closed tube round a loop (a rolled rim, a hoop) */
  loopTube(loop: V3[], r: number, segs = 10) {
    this.tube([...loop, loop[0], loop[1]], r, segs, false)
  }
  /** cubic Bézier samples */
  static bez(p0: V3, p1: V3, p2: V3, p3: V3, n = 16): V3[] {
    const out: V3[] = []
    for (let i = 0; i <= n; i++) {
      const t = i / n, s = 1 - t
      out.push(add(add(mul(p0, s * s * s), mul(p1, 3 * s * s * t)), add(mul(p2, 3 * s * t * t), mul(p3, t * t * t))))
    }
    return out
  }
  finish(b: MeshBuilder, matOf: (role: string) => string | null): SolidGroup[] {
    const groups: SolidGroup[] = []
    for (const [role, m] of this.by) {
      if (!m.vertCount) continue
      const start = b.idx.length, base = b.vertCount
      // element-wise, never push(...big) — a chandelier's arrays overflow the call stack
      for (let i = 0; i < m.pos.length; i++) b.pos.push(m.pos[i])
      for (let i = 0; i < m.nrm.length; i++) b.nrm.push(m.nrm[i])
      for (const i of m.idx) b.idx.push(i + base)
      b.vertCount += m.vertCount
      groups.push({ start, count: m.idx.length, materialId: matOf(role) })
    }
    return groups
  }
}

